import { styled } from '@mui/material/styles'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
  Button,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
} from '@mui/material'
import {
  SEVV_DIALOG_TITLE_BACKGROUND,
  SEVV_DIALOG_TITLE_BORDER_BOTTOM,
  SEVV_DIALOG_TITLE_COLOR,
} from '../../../theme/sevvDialogChrome'

// Styled Dialog Component
export const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    background: var(--sevv-bg-paper, #ffffff);
    border: 1px solid var(--sevv-divider, #eeeeee);
    border-radius: 20px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(90deg, transparent, color-mix(in srgb, var(--sevv-primary, #f97415) 50%, transparent), transparent);
    }
  }
`

export const StyledDialogTitle = styled(DialogTitle)`
  background: ${SEVV_DIALOG_TITLE_BACKGROUND};
  border-bottom: ${SEVV_DIALOG_TITLE_BORDER_BOTTOM};
  padding: 20px 24px !important;
  color: ${SEVV_DIALOG_TITLE_COLOR};
  font-weight: 600;
  font-size: 18px;
  display: flex;
  align-items: center;
  gap: 12px;
`

export const StyledDialogContent = styled(DialogContent)`
  padding: 24px;
  background-color: var(--sevv-bg-paper, #ffffff);
  
  .MuiTextField-root {
    .MuiInputBase-root {
      background-color: var(--sevv-bg-paper, #ffffff);
      border-radius: 8px;
      
      &:hover {
        background-color: var(--sevv-bg-paper, #ffffff);
      }
      
      &.Mui-focused {
        background-color: var(--sevv-bg-paper, #ffffff);
      }
    }
    
    .MuiInputLabel-root {
      color: var(--sevv-text-secondary, #757575);
      
      &.Mui-focused {
        color: var(--sevv-primary, #f97415);
      }
    }
    
    .MuiOutlinedInput-notchedOutline {
      border-color: var(--sevv-divider, #eeeeee);
      
      &:hover {
        border-color: color-mix(in srgb, var(--sevv-primary, #f97415) 50%, transparent);
      }
      
      &.Mui-focused {
        border-color: var(--sevv-primary, #f97415);
      }
    }
    
    .MuiInputBase-input {
      color: var(--sevv-text-primary, #424242);
      
      &::placeholder {
        color: var(--sevv-gray-400, #bdbdbd);
      }
    }
  }
  
  .MuiFormControl-root {
    .MuiInputBase-root {
      background-color: var(--sevv-bg-paper, #ffffff);
      border-radius: 8px;
      
      &:hover {
        background-color: var(--sevv-bg-paper, #ffffff);
      }
      
      &.Mui-focused {
        background-color: var(--sevv-bg-paper, #ffffff);
      }
    }
    
    .MuiInputLabel-root {
      color: var(--sevv-text-secondary, #757575);
      
      &.Mui-focused {
        color: var(--sevv-primary, #f97415);
      }
    }
    
    .MuiOutlinedInput-notchedOutline {
      border-color: var(--sevv-divider, #eeeeee);
      
      &:hover {
        border-color: color-mix(in srgb, var(--sevv-primary, #f97415) 50%, transparent);
      }
      
      &.Mui-focused {
        border-color: var(--sevv-primary, #f97415);
      }
    }
    
    .MuiSelect-select {
      color: var(--sevv-text-primary, #424242);
    }
  }
`

export const StyledDialogActions = styled(DialogActions)`
  padding: 16px 24px;
  background: var(--sevv-bg-default, #fafafa);
  border-top: 1px solid var(--sevv-divider, #eeeeee);
  
  .MuiButton-root {
    border-radius: 8px;
    text-transform: none;
    font-weight: 600;
    padding: 8px 16px;
    transition: all 0.2s ease;
    
    &.MuiButton-contained {
      background: linear-gradient(90deg, var(--sevv-primary, #f97415), var(--sevv-primary-light, #ffa047));
      box-shadow: var(--sevv-shadow-primary, 0 4px 14px 0 rgba(249, 116, 21, 0.3));
      
      &:hover {
        background: linear-gradient(90deg, var(--sevv-primary-light, #ffa047), color-mix(in srgb, var(--sevv-primary-light, #ffa047) 70%, var(--sevv-white, #ffffff)));
        box-shadow: var(--sevv-shadow-primary-lg, 0 10px 25px 0 rgba(249, 116, 21, 0.4));
        transform: translateY(-2px);
      }
      
      &:active {
        transform: translateY(0);
      }
    }
    
    &.MuiButton-outlined {
      border-color: color-mix(in srgb, var(--sevv-primary, #f97415) 30%, transparent);
      color: var(--sevv-primary, #f97415);
      
      &:hover {
        background: color-mix(in srgb, var(--sevv-primary, #f97415) 5%, transparent);
        border-color: color-mix(in srgb, var(--sevv-primary, #f97415) 50%, transparent);
      }
    }
  }
`

export const StyledTableContainer = styled(TableContainer)`
  background: var(--sevv-bg-paper, #ffffff);
  border-radius: 12px;
  border: 1px solid var(--sevv-divider, #eeeeee);
  overflow: hidden;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  
  .MuiTable-root {
    background: transparent;
  }
  
  .MuiTableHead-root {
    background: color-mix(in srgb, var(--sevv-primary, #f97415) 10%, transparent);
    border-bottom: 1px solid color-mix(in srgb, var(--sevv-primary, #f97415) 20%, transparent);
    
    .MuiTableCell-head {
      color: var(--sevv-primary, #f97415);
      font-weight: 600;
      font-size: 0.875rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      border-bottom: 1px solid color-mix(in srgb, var(--sevv-primary, #f97415) 20%, transparent);
      padding: 16px 12px;
    }
  }
  
  .MuiTableBody-root {
    .MuiTableRow-root {
      transition: all 0.2s ease;
      border-bottom: 1px solid var(--sevv-divider, #eeeeee);
      
      &:hover {
        background: color-mix(in srgb, var(--sevv-primary, #f97415) 5%, transparent);
        transform: translateX(4px);
      }
      
      &:last-child {
        border-bottom: none;
      }
      
      .MuiTableCell-body {
        color: var(--sevv-text-primary, #424242);
        padding: 12px;
        border-bottom: none;
        
        .MuiTypography-root {
          color: inherit;
        }
        
        .MuiChip-root {
          font-weight: 500;
          border-radius: 6px;
        }
        
        .MuiIconButton-root {
          color: var(--sevv-text-secondary, #757575);
          transition: all 0.2s ease;
          
          &:hover {
            color: var(--sevv-primary, #f97415);
            background: color-mix(in srgb, var(--sevv-primary, #f97415) 10%, transparent);
            transform: scale(1.1);
          }
        }
      }
    }
  }
`

export const StyledButton = styled(Button)`
  border-radius: 8px;
  text-transform: none;
  font-weight: 600;
  padding: 8px 16px;
  transition: all 0.2s ease;
  
  &.MuiButton-contained {
    background: linear-gradient(90deg, var(--sevv-primary, #f97415), var(--sevv-primary-light, #ffa047));
    box-shadow: var(--sevv-shadow-primary, 0 4px 14px 0 rgba(249, 116, 21, 0.3));
    
    &:hover {
      background: linear-gradient(90deg, var(--sevv-primary-light, #ffa047), color-mix(in srgb, var(--sevv-primary-light, #ffa047) 70%, var(--sevv-white, #ffffff)));
      box-shadow: var(--sevv-shadow-primary-lg, 0 10px 25px 0 rgba(249, 116, 21, 0.4));
      transform: translateY(-2px);
    }
    
    &:active {
      transform: translateY(0);
    }

    /* The gradient above outranks MUI's own disabled styling, so a disabled button
       kept full brand colour and read as clickable. Stated explicitly here. [SC-313] */
    &.Mui-disabled {
      background: var(--sevv-gray-200, #eeeeee);
      color: var(--sevv-gray-400, #bdbdbd);
      box-shadow: none;
      transform: none;
    }
  }
  
  &.MuiButton-outlined {
    border-color: var(--sevv-primary, #f97415);
    border-width: 2px;
    color: var(--sevv-primary, #f97415);
    font-weight: 500;
    min-height: 36px;
    
    &:hover {
      background: color-mix(in srgb, var(--sevv-primary, #f97415) 10%, transparent);
      border-color: var(--sevv-primary-light, #ffa047);
      border-width: 2px;
    }

    &.Mui-disabled {
      border-color: var(--sevv-gray-200, #eeeeee);
      color: var(--sevv-gray-400, #bdbdbd);
    }
  }
`

export const StyledIconButton = styled(IconButton)`
  color: var(--sevv-text-secondary, #757575);
  transition: all 0.2s ease;
  border-radius: 8px;
  
  &:hover {
    color: var(--sevv-primary, #f97415);
    background: color-mix(in srgb, var(--sevv-primary, #f97415) 10%, transparent);
    transform: scale(1.1);
  }

  /* Same reason as StyledButton: the base colour above hid the disabled state. */
  &.Mui-disabled {
    color: var(--sevv-gray-400, #bdbdbd);
  }
`

export const StyledChip = styled(Chip)`
  font-weight: 500;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
`

export const StyledSwitch = styled(Switch)`
  .MuiSwitch-switchBase.Mui-checked {
    color: var(--sevv-primary, #f97415);
    
    &:hover {
      backgroundColor: color-mix(in srgb, var(--sevv-primary, #f97415) 8%, transparent);
    }
  }
  
  .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track {
    backgroundColor: var(--sevv-primary, #f97415);
  }
`

export const StyledFormControlLabel = styled(FormControlLabel)`
  .MuiFormControlLabel-label {
    color: var(--sevv-text-primary, #424242);
  }
` 